import React, { useEffect, useRef, useState } from 'react';
import './DriverLogPage.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import {
  getFilterData,
  getJsonFromStorage,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getDriverData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { DRIVER_LOG_MODULE } from '../../../configs/moduleNames';
import { loadDriverCardById, loadDriverLogList, loadDriverLogListAll } from '../actions';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInput from '../../../components/fields/TextInput/TextInput';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { DriverLogDetails } from './DriverLogDetails';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import { CITIZENSHIP_TYPE_LIST } from '../../../configs';
import getDefaultSort from '../../../utils/getDefaultSort';
import { dateFormatter, userFIOFormatter } from '../../../utils/formatters';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import { closeAllPopupWrapper } from '../../InformationScreenWrapper/actions';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';

export default function DriverLogPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const {
    driverLogList,
    loadingDriverLogList,
    driverLogListMeta,
    loadingDriverLogListAll,
    driverCard,
  } = useSelector(getDriverData);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterName = 'driver-log-filter';
  const [selectedRow, setSelectedRow] = useState(false);

  const driver_id = searchParams.get('driver_id');

  const { formatMessage } = useIntl();
  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[DRIVER_LOG_MODULE].driverLogListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadDriverLogList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  const defaultFilterValues = {
    search: '', created_at: '',
  };

  useEffect(() => {
    if (driver_id) {
      dispatch(closeAllPopupWrapper());
      setSelectedRow({ id: driver_id });
    }
  }, [driver_id]);

  useEffect(() => reset(getFilterData(filterName, defaultFilterValues)), []);
  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getJsonFromStorage(filterName, defaultFilterValues) });

  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const moreFormatter = (_, item) => null;

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];

    return classes.join(' ');
  };

  const [selected, setSelected] = useState([]);
  const [isAllPageRowsSelected, setIsAllPageRowsSelected] = useState(false);

  useEffect(() => {
    if (isAllPageRowsSelected) {
      dispatch(loadDriverLogListAll({
        filter: getValues(),
      })).then(data => {
        setSelected(data?.data.map(e => e.id));
      }).catch(responseLogWithNotification);
    }
  }, [isAllPageRowsSelected]);

  const loadDriver = () => {
    dispatch(loadDriverCardById(driver_id));
  };

  useEffect(() => {
    if (driver_id) {
      loadDriver();
    }
  }, [driver_id]);

  useEffect(() => () => dispatch((closeAllPopupWrapper())), []);

  const driverColumns = [
    {
      dataField: 'driver_name',
      text: 'ФИО',
      headerClassName: 'driver-name-column',
      formatter: (_, item) => userFIOFormatter({ last_name: item?.driver_last_name, first_name: item?.driver_first_name, middle_name: item?.driver_middle_name }),
      sortField: 'driver_name',
    },
    {
      dataField: 'driver_citizenship',
      text: 'Гражданство',
      headerClassName: 'citizenship-column',
      formatter: item => formatMessage({ id: `citizenship.type.${item}` }),
    },
    {
      dataField: 'driver_inn',
      text: 'ИНН',
      headerClassName: 'inn-column',
    },
    {
      dataField: 'driver_license_experience',
      text: 'Стаж',
      headerClassName: 'experience-column',
    },
    {
      dataField: 'driver_license',
      text: 'Водительское удостоверение',
      formatter: (e, item) => `${item?.driver_license_series || ''} ${item?.driver_license_number || ''} `,
      headerClassName: 'driver-license-column',
    },
    {
      dataField: 'driver_license_valid_until_date',
      text: 'Срок действия водительского удостоверения',
      headerClassName: 'license-expiry-column',
      formatter: dateFormatter,
    },
  ];

  const [sort, setSort] = useState(getDefaultSort(driverColumns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  return (
    <div className="page">
      <TimerLoader global loading={loadingDriverLogList || loadingDriverLogListAll} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.driver' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextSearchInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
          />
          <FieldDTO
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={{
              name: 'driver_citizenship',
              type: Number,
              labelKey: 'citizenship',
            }}
            placeholder="Гражданство"
            className="no-border"
            addPlaceholder
            options={CITIZENSHIP_TYPE_LIST}
          />
          <FieldDTO
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={{
              name: 'driver_license_valid_until_date',
              type: Number,
              labelKey: 'driver_license_valid_until_date',
            }}
            placeholder="Срок действия удостоверения"
            className="no-border"
            addPlaceholder
            options={[
              { value: 1, name: 'Заканчивается в течение 1 месяца' },
              { value: 2, name: 'Заканчивается в течение 3 месяцев' },
            ]}
          />
          <FieldDTO
            control={control}
            type="text"
            multiple
            component={CustomSelectInput}
            dtoObject={{
              name: 'driver_license_experience',
              type: Number,
              labelKey: 'driver_license_experience',
            }}
            placeholder="Стаж"
            className="no-border"
            addPlaceholder
            options={Array.from({ length: 100 }, (_, i) => ({ value: i + 1, name: String(i + 1) }))}
          />
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={driverLogList}
                columns={driverColumns}
                name="driver-list"
                changeSort={changeSort}
                sort={sort}
                additionalColumns={[]}
                onDoubleClickByRow={item => { setSelectedRow(item); setSearchParams({ driver_id: item.id }); }}
                settingsFormatter={moreFormatter}
                rowClassFormatter={rowClassFormatter}
              />
            </div>
          </div>
          <Pagination meta={driverLogListMeta} list={driverLogList} handleChangeMeta={loadData} />
        </div>
      </div>
      {selectedRow && driverCard ? <DriverLogDetails onClose={() => setSelectedRow(false)} id={selectedRow?.id} /> : null}
    </div>
  );
}
